/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useRef } from 'react'

import type { ECharts } from 'echarts'

interface Rect {
  x: number
  y: number
  width: number
  height: number
}

/** The slice of ECharts' internal model this needs. getModel is not in the public typings,
 *  but it is the only way to read a grid's laid-out rectangle. */
interface ChartWithModel {
  getModel(): {
    getComponent(mainType: string, index: number): { coordinateSystem?: { getRect(): Rect } } | undefined
  }
}

/** Laid-out rectangles of every grid, top to bottom. */
const gridRects = (chart: ECharts): Rect[] => {
  const model = (chart as unknown as ChartWithModel).getModel()
  const rects: Rect[] = []
  for (let i = 0; ; i++) {
    const rect = model.getComponent('grid', i)?.coordinateSystem?.getRect()
    if (!rect) return rects
    rects.push(rect)
  }
}

/** Gap between the pointer and the popover's corner. */
const POPOVER_OFFSET = 12

/**
 * A vertical line that follows the pointer from the top of the temperature grid to the
 * bottom of the rows under it, so a bar can be read against the temperatures above it, and
 * a popover beside the pointer with what `describe` says about that instant (time, address,
 * geofence). The popover gives way to the chart's own tooltip: it hides while an item
 * tooltip (a bar, a temperature point, a geofence band) is showing.
 *
 * DOM overlays rather than ECharts' axisPointer: the axisPointer draws one segment per
 * grid, broken across the time labels between them, and only shows with an axis-triggered
 * tooltip, where this chart's tooltips are per item. The overlays are pointer-events none,
 * so those tooltips keep working, and they're moved by direct style writes on animation
 * frames, so the pointer never re-renders React.
 */
export const useCursorLine = (describe: (time: number) => string[]) => {
  const lineRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const attached = useRef<ECharts | null>(null)
  // Read at hover time, so a new describe (new data, zone) needs no rebinding.
  const describeRef = useRef(describe)
  describeRef.current = describe

  const attach = useCallback((chart: ECharts) => {
    if (attached.current === chart) return
    attached.current = chart
    let frame = 0
    let tooltipShowing = false
    let last: { x: number; y: number } | null = null

    const hide = () => {
      cancelAnimationFrame(frame)
      last = null
      if (lineRef.current) lineRef.current.style.display = 'none'
      if (popoverRef.current) popoverRef.current.style.display = 'none'
    }

    const draw = () => {
      const line = lineRef.current
      const popover = popoverRef.current
      const rects = gridRects(chart)
      if (!line || !popover || !rects.length || !last) return
      const { x, y } = last
      const first = rects[0]
      const lastRect = rects[rects.length - 1]
      const top = first.y
      const bottom = lastRect.y + lastRect.height
      if (x < first.x || x > first.x + first.width || y < top || y > bottom) {
        line.style.display = 'none'
        popover.style.display = 'none'
        return
      }
      line.style.display = 'block'
      line.style.left = `${x}px`
      line.style.top = `${top}px`
      line.style.height = `${bottom - top}px`

      const time = chart.convertFromPixel({ xAxisIndex: 0 }, x)
      const lines = tooltipShowing || typeof time !== 'number' ? [] : describeRef.current(time)
      if (!lines.length) {
        popover.style.display = 'none'
        return
      }
      popover.replaceChildren(
        ...lines.map((text, i) => {
          const div = document.createElement('div')
          div.textContent = text
          if (i === 0) div.style.fontWeight = '600'
          return div
        })
      )
      popover.style.display = 'block'
      // Right of the pointer, flipping left near the edge; below it, flipping up near the bottom.
      const width = popover.offsetWidth
      const height = popover.offsetHeight
      const left = x + POPOVER_OFFSET + width > chart.getWidth() ? x - POPOVER_OFFSET - width : x + POPOVER_OFFSET
      const below = y + POPOVER_OFFSET + height > chart.getHeight() ? y - POPOVER_OFFSET - height : y + POPOVER_OFFSET
      popover.style.left = `${Math.max(0, left)}px`
      popover.style.top = `${Math.max(0, below)}px`
    }

    const redraw = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(draw)
    }

    const zr = chart.getZr()
    zr.on('mousemove', (e: { offsetX: number; offsetY: number }) => {
      last = { x: e.offsetX, y: e.offsetY }
      redraw()
    })
    zr.on('globalout', hide)
    // ECharts dispatches these as the item tooltip comes and goes.
    chart.on('showTip', () => {
      tooltipShowing = true
      redraw()
    })
    chart.on('hideTip', () => {
      tooltipShowing = false
      redraw()
    })
  }, [])

  return { lineRef, popoverRef, attach }
}
